import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton, useTheme, alpha } from '@mui/material';
import { Description, Assignment, AttachMoney, TrendingUp } from '@mui/icons-material';

export interface MetricCardData {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  color?: string;
  subtitle?: string;
  subtitles?: string[]; // Support multiple subtitle lines
}

interface MetricCardsProps {
  metrics: MetricCardData[];
  loading?: boolean;
}

/**
 * MetricCards - Dashboard-style metric display following Material Design best practices
 *
 * Features:
 * - Color-coded cards for visual hierarchy
 * - Responsive grid layout
 * - Loading skeletons
 * - Subtle elevation and hover effects
 * - Icons for quick visual identification
 */
export const MetricCards: React.FC<MetricCardsProps> = ({ metrics, loading = false }) => {
  const theme = useTheme();

  const defaultIcons = [
    <Description key="icon-0" />,
    <Assignment key="icon-1" />,
    <AttachMoney key="icon-2" />,
    <TrendingUp key="icon-3" />,
  ];

  const defaultColors = [
    theme.palette.primary.main,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: 2,
        mb: 2,
      }}
    >
      {metrics.map((metric, index) => {
        const color = metric.color || defaultColors[index % defaultColors.length];
        const icon = metric.icon || defaultIcons[index % defaultIcons.length];

        return (
          <Card
            key={metric.label}
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'visible',
              borderRadius: 2,
              bgcolor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.6)
                  : theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              transition: theme.transitions.create(['box-shadow', 'transform'], {
                duration: theme.transitions.duration.shorter,
              }),
              '&:hover': {
                boxShadow: theme.shadows[4],
                transform: 'translateY(-2px)',
              },
            }}
          >
            <CardContent
              sx={{
                p: 2,
                '&:last-child': { pb: 2 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      mb: 0.5,
                    }}
                  >
                    {metric.label}
                  </Typography>
                  {loading ? (
                    <Skeleton width="80%" height={32} />
                  ) : (
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        color: 'text.primary',
                        lineHeight: 1.2,
                      }}
                    >
                      {metric.value}
                    </Typography>
                  )}
                  {metric.subtitle && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                        display: 'block',
                        mt: 0.5,
                      }}
                    >
                      {metric.subtitle}
                    </Typography>
                  )}
                  {metric.subtitles &&
                    metric.subtitles.map((subtitle, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          display: 'block',
                          mt: idx === 0 ? 0.5 : 0.25,
                        }}
                      >
                        {subtitle}
                      </Typography>
                    ))}
                </Box>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(color, 0.1),
                    color: color,
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

/**
 * Helper function to format numbers for display
 */
export const formatMetricValue = (
  value: number,
  type: 'number' | 'currency' | 'percent' = 'number',
): string => {
  if (type === 'currency') {
    // Return empty string for zero values to clean up table appearance
    if (value === 0) {
      return '';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  if (type === 'percent') {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value / 100);
  }

  return new Intl.NumberFormat('en-US').format(value);
};
